import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { formatVienna } from '../../../../shared/datetime'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'

import { certificateFileName, downloadCertificate } from '../Courses/data'

import styles from './style.module.scss'

/*
 * Ein Kurs von TalentsLounge, eingebettet. Die Einbett-Adresse mit
 * Startschlüssel holt der Server bei jedem Öffnen neu (POST tl-start): Der
 * Schlüssel gilt zehn Minuten und genau einmal.
 *
 * Den Abschluss meldet TalentsLounge an den Server, nicht an diese Seite.
 * Hier wird nur nachgesehen, ob er schon angekommen ist — beim Öffnen, wenn
 * das Fenster wieder in den Vordergrund kommt, und jede Minute.
 */
export default function TlCoursePlayer() {
	const { t } = useTranslation()
	const history = useHistory()
	const { id } = useParams()
	const [adresse, setAdresse] = useState(null)
	const [fehler, setFehler] = useState(null)
	const [kurs, setKurs] = useState(null)
	const [holt, setHolt] = useState(false)
	const versuch = useRef(0)

	const starten = useCallback(() => {
		setFehler(null)
		setAdresse(null)
		versuch.current += 1
		const dieser = versuch.current
		http.post(TApi.TL_COURSE_START(id), {}, { disableToast: true })
			.then(({ data }) => dieser === versuch.current && setAdresse(data.url))
			// Ohne Satz vom Server steht beim Anzeigen der allgemeine — `t` gehört nicht in die Abhängigkeiten.
			.catch(e => dieser === versuch.current && setFehler(e?.response?.data?.message || true))
	}, [id])

	const standHolen = useCallback(() => {
		http.get(TApi.TL_COURSES, { disableToast: true })
			.then(({ data }) => setKurs((data?.courses || []).find(k => String(k.id) === String(id)) || null))
			.catch(() => {})
	}, [id])

	useEffect(() => {
		starten()
		standHolen()
	}, [starten, standHolen])

	useEffect(() => {
		const takt = window.setInterval(standHolen, 60000)
		window.addEventListener('focus', standHolen)
		return () => {
			window.clearInterval(takt)
			window.removeEventListener('focus', standHolen)
		}
	}, [standHolen])

	const fertig = kurs?.progress?.completed

	const urkunde = () => {
		setHolt(true)
		downloadCertificate(TApi.COURSE_CERTIFICATE(id), certificateFileName(kurs?.title)).finally(() => setHolt(false))
	}

	return (
		<div className={styles.playerRoot}>
			<div className={styles.playerHead}>
				<Btn theme="minimal" onClick={() => history.push(TRoutes.TL_COURSES)}>
					← {t('Back to the courses')}
				</Btn>
				<span className={styles.playerTitle}>{kurs?.title}</span>
				<span className={styles.playerStatus}>
					{fertig ? (
						<>
							✓ {t('Completed on {{date}}', { date: formatVienna(kurs.progress.completedAt, 'dd.MM.yyyy') })}{' '}
							<Btn theme="primary" size="small" disabled={holt} onClick={urkunde}>
								{holt ? t('Preparing…') : t('Download certificate')}
							</Btn>
						</>
					) : (
						t('As soon as you have passed all three module quizzes and finished every lesson, your certificate appears here.')
					)}
				</span>
			</div>

			{fehler && (
				<div className={styles.playerError}>
					<p>{fehler === true ? t('The course could not be started.') : fehler}</p>
					<Btn theme="secondary" onClick={starten}>
						{t('Try again')}
					</Btn>
				</div>
			)}
			{!fehler && !adresse && (
				<div className={styles.playerLoading}>
					<Loading />
					<p>{t('The course is starting…')}</p>
				</div>
			)}
			{adresse && (
				<iframe
					key={adresse}
					className={styles.frame}
					src={adresse}
					title={kurs?.title || t('Course')}
					allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
					allowFullScreen
				/>
			)}
		</div>
	)
}
